import { useMemo } from "react";
import { useProjectsQuery, useProjectsQueryScope } from "@/features/projects/hooks/useProjectsQuery";
import { useQueryClient } from "@tanstack/react-query";
import { projectQueryKeys } from "@/features/projects/query-keys";
import type { Project as CanonicalProject } from "@/features/projects/types";

export type Project = CanonicalProject & {
  displayName?: string;
};

type Options = {
  enabled?: boolean;
};

const sortProjects = (projects: Project[]): Project[] =>
  [...projects].sort((left, right) => {
    const leftName = left.name ?? left.displayName ?? "";
    const rightName = right.name ?? right.displayName ?? "";
    return leftName.localeCompare(rightName);
  });

export const useProjects = ({ enabled = true }: Options = {}) => {
  const { scope } = useProjectsQueryScope();
  const client = useQueryClient();
  const query = useProjectsQuery(
    { includeShared: true },
    { enabled },
  );
  const data = useMemo(
    () => (query.data ? sortProjects(query.data) : query.data),
    [query.data],
  );

  const queryKey = projectQueryKeys.list(scope, { includeShared: true });
  const isInvalidated = Boolean(query.isStale && client.getQueryState(queryKey)?.isInvalidated);
  return { ...query, data, isInvalidated };
};
